import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const OfflineContext = createContext(null);

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) throw new Error('useOffline must be used within OfflineProvider');
  return context;
};

const OFFLINE_QUEUE_KEY = 'destiny_offline_queue';
const LAST_SYNC_KEY = 'destiny_last_sync';

const getAuthHeaders = () => {
  const token = localStorage.getItem('session_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

export const OfflineProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [offlineQueue, setOfflineQueue] = useState([]);
  const [lastSync, setLastSync] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    const savedQueue = localStorage.getItem(OFFLINE_QUEUE_KEY);
    const savedSync = localStorage.getItem(LAST_SYNC_KEY);
    if (savedQueue) setOfflineQueue(JSON.parse(savedQueue));
    if (savedSync) setLastSync(new Date(savedSync));
  }, []);

  useEffect(() => {
    localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(offlineQueue));
  }, [offlineQueue]);

  const syncOfflineData = useCallback(async () => {
    if (offlineQueue.length === 0 || isSyncing) return;
    setIsSyncing(true);
    const failedItems = [];

    for (const item of offlineQueue) {
      try {
        let url = `${API_URL}/api`;
        let method = 'POST';
        let body = {};

        switch (item.type) {
          case 'CLOCK_IN':
            url += '/attendance/clock-in';
            body = { branch_id: item.branch_id, latitude: item.latitude, longitude: item.longitude, verification_method: item.verification_method };
            break;
          case 'CLOCK_OUT':
            url += '/attendance/clock-out';
            body = { latitude: item.latitude, longitude: item.longitude, verification_method: item.verification_method };
            break;
          case 'LEAVE_APPLICATION':
            url += '/leave';
            body = item.data;
            break;
          default:
            continue;
        }

        const response = await fetch(url, {
          method,
          headers: getAuthHeaders(),
          body: JSON.stringify(body)
        });

        if (!response.ok) {
          failedItems.push(item);
        }
      } catch (error) {
        console.error('Sync failed for:', item.id, error);
        failedItems.push(item);
      }
    }

    setOfflineQueue(failedItems);
    setLastSync(new Date());
    localStorage.setItem(LAST_SYNC_KEY, new Date().toISOString());
    setIsSyncing(false);

    // Try background sync if available
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      const reg = await navigator.serviceWorker.ready;
      await reg.sync.register('sync-attendance').catch(() => {});
    }
  }, [offlineQueue, isSyncing]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      syncOfflineData();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen for SW sync complete
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'SYNC_COMPLETE') {
          setOfflineQueue([]);
          setLastSync(new Date());
        }
      });
    }

    // Prefetch key data for offline access when online
    if (navigator.onLine) {
      const prefetchForOffline = async () => {
        const token = localStorage.getItem('session_token');
        if (!token) return;
        const headers = { 'Authorization': `Bearer ${token}` };
        const urls = [
          `${API_URL}/api/branches`,
          `${API_URL}/api/holidays`,
          `${API_URL}/api/announcements`,
        ];
        for (const url of urls) {
          try {
            const res = await fetch(url, { headers });
            if (res.ok) {
              const data = await res.json();
              localStorage.setItem(`destiny_cache_${url}`, JSON.stringify({ data, timestamp: new Date().toISOString() }));
            }
          } catch {}
        }
      };
      prefetchForOffline();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncOfflineData]);

  const addToQueue = useCallback((action) => {
    const queueItem = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      ...action
    };
    setOfflineQueue(prev => [...prev, queueItem]);
    return queueItem.id;
  }, []);

  const removeFromQueue = useCallback((id) => {
    setOfflineQueue(prev => prev.filter(item => item.id !== id));
  }, []);

  const cacheData = useCallback((key, data) => {
    try {
      localStorage.setItem(`destiny_cache_${key}`, JSON.stringify({
        data,
        timestamp: new Date().toISOString()
      }));
    } catch (error) {
      console.error('Cache error:', error);
    }
  }, []);

  const getCachedData = useCallback((key, maxAge = 3600000) => {
    try {
      const cached = localStorage.getItem(`destiny_cache_${key}`);
      if (!cached) return null;
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - new Date(timestamp).getTime() > maxAge) {
        localStorage.removeItem(`destiny_cache_${key}`);
        return null;
      }
      return data;
    } catch (error) {
      return null;
    }
  }, []);

  return (
    <OfflineContext.Provider value={{
      isOnline,
      offlineQueue,
      lastSync,
      isSyncing,
      addToQueue,
      removeFromQueue,
      syncOfflineData,
      cacheData,
      getCachedData,
      pendingCount: offlineQueue.length
    }}>
      {children}
    </OfflineContext.Provider>
  );
};

export default OfflineContext;
